import React from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField } from "@mui/material";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import colors from "assets/theme-dark/base/colors";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";

interface PromoteDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (name: string) => void;
  promoteName: string;
  setPromoteName: React.Dispatch<React.SetStateAction<string>>;
}

const PromoteDialog: React.FC<PromoteDialogProps> = ({ open, onClose, onSubmit, promoteName, setPromoteName }) => {
  const handleSubmit = () => {
    if (promoteName.trim()) {
      onSubmit(promoteName);
      setPromoteName("");
    }
  };

  return (
    <StyledDialog open={open} onClose={onClose} aria-labelledby="promote-dialog-title" component={motion.div}>
      <StyledDialogTitle id="promote-dialog-title">
        <MDTypography variant="h6" fontWeight="bold" color="text">
          Promote to Matcher
        </MDTypography>
      </StyledDialogTitle>
      <StyledDialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Name"
          fullWidth
          variant="outlined"
          value={promoteName}
          onChange={(e) => setPromoteName(e.target.value)}
          sx={{
            "& .MuiOutlinedInput-root": {
              "& fieldset": { borderColor: colors.text.main },
              "&:hover fieldset": { borderColor: colors.primary.main },
              "&.Mui-focused fieldset": { borderColor: colors.primary.main },
              color: colors.text.main,
            },
            "& .MuiInputBase-input": { color: colors.text.main },
            "& .MuiInputLabel-root": { color: colors.grey[500] },
            "& .MuiInputLabel-root.Mui-focused": { color: colors.grey[500] },
          }}
        />
      </StyledDialogContent>
      <StyledDialogActions>
        <MDButton
          onClick={onClose}
          variant="outlined"
          sx={{
            color: colors.text.main,
            borderColor: colors.grey[700],
            borderWidth: "1px",
            borderStyle: "solid",
            padding: "8px 16px",
            borderRadius: "8px",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: colors.grey[300],
              borderColor: colors.grey[600],
              transform: "translateY(-1px)",
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
            },
            "&:active": {
              transform: "translateY(0)",
              boxShadow: "none",
            },
          }}
        >
          Cancel
        </MDButton>
        <MDButton
          onClick={handleSubmit}
          variant="contained"
          disabled={!promoteName.trim()}
          sx={{
            background: `linear-gradient(45deg, ${colors.secondary.main} 30%, ${colors.secondary.focus} 90%)`,
            color: colors.white.main,
            padding: "8px 16px",
            borderRadius: "8px",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.3)",
            transition: "all 0.2s ease",
            "&:hover": {
              background: `linear-gradient(45deg, ${colors.secondary.focus} 30%, ${colors.secondary.main} 90%)`,
              transform: "translateY(-1px)",
              boxShadow: "0 6px 12px rgba(0, 0, 0, 0.4)",
            },
            "&:active": {
              transform: "translateY(0)",
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
            },
            "&:disabled": {
              background: colors.grey[700],
              color: colors.grey[500],
              boxShadow: "none",
            },
          }}
        >
          <MDTypography variant="button" fontWeight="medium" color="white">
            Submit
          </MDTypography>
        </MDButton>
      </StyledDialogActions>
    </StyledDialog>
  );
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    background: `linear-gradient(145deg, ${colors.background.card} 0%, ${colors.background.default} 100%)`,
    borderRadius: "12px",
    color: colors.text.main,
    minWidth: "320px",
    maxWidth: "480px",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.3), 0 4px 12px rgba(0, 0, 0, 0.2)",
    overflow: "hidden",
    transition: "all 0.3s ease",
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: colors.background.default,
  color: colors.text.main,
  fontWeight: "bold",
  padding: "16px 24px",
  display: "flex",
  alignItems: "center",
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: "20px 24px",
  background: colors.background.card,
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: "16px 24px",
  background: colors.background.default,
  display: "flex",
  gap: "12px",
  justifyContent: "flex-end",
}));

export default PromoteDialog;
